import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Package, Truck, MapPin, User, CreditCard, Ban, RotateCcw, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { ShippingPanel } from "@/components/admin/ShippingPanel";
import { paymentPill, fulfillmentPill } from "@/components/admin/OrdersTab";

const FLOW = ["confirmed", "packed", "shipped", "out_for_delivery", "delivered"];
const FLOW_LABELS = { confirmed: "Confirmed", packed: "Packed", shipped: "Shipped", out_for_delivery: "Out for delivery", delivered: "Delivered" };
const NEXT = { pending: "confirmed", awaiting_confirmation: "confirmed", confirmed: "packed", packed: "shipped", shipped: "out_for_delivery", out_for_delivery: "delivered" };

const Card = ({ title, icon: Icon, children, testid }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid={testid}>
    <p className="text-sm font-medium text-[#111827] mb-4 flex items-center gap-2">{Icon && <Icon size={15} className="text-[#E80071]" />}{title}</p>
    {children}
  </div>
);

export const OrderDetail = ({ order, onBack, onSaved }) => {
  const [courier, setCourier] = useState(order.courier_partner || "");
  const [trackingId, setTrackingId] = useState(order.tracking_id || "");
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || "");
  const [busy, setBusy] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [refundAmt, setRefundAmt] = useState(String(order.total));

  const update = async (payload, msg) => {
    setBusy(true);
    try {
      await api.put(`/admin/orders/${order.id}`, payload);
      toast.success(msg);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const advance = () => update({ status: NEXT[order.status] }, `Order marked ${FLOW_LABELS[NEXT[order.status]]}`);
  const saveShipping = () =>
    update({ courier_partner: courier || null, tracking_id: trackingId || null, tracking_url: trackingUrl || null }, "Shipping details saved");
  const cancel = () => {
    if (window.confirm("Cancel this order? The customer will be notified by email.")) update({ status: "cancelled" }, "Order cancelled");
  };
  const markReturned = () => {
    const amt = parseFloat(refundAmt);
    if (isNaN(amt) || amt < 0 || amt > order.total) {
      toast.error(`Refund must be between ₹0 and ${inr(order.total)}`);
      return;
    }
    update({ status: "returned", refund_amount: amt }, "Order marked returned — stock restored");
    setShowRefund(false);
  };

  const phone = (order.shipping_address?.phone || order.customer_phone || "").replace(/\D/g, "").replace(/^0+/, "");
  const waPhone = phone.length === 10 ? `91${phone}` : phone;
  const confirmLink = `${window.location.origin}/api/orders/${order.id}/cod-confirm?token=${order.confirm_token || ""}`;
  const waText = encodeURIComponent(
    `Hi ${order.customer_name || "there"}! Your ViRa COD order #${order.id.slice(0, 8).toUpperCase()} (₹${order.total?.toLocaleString("en-IN")}) needs a quick confirmation before we ship: ${confirmLink}`
  );

  const pay = paymentPill(order.status, order.payment_method);
  const ful = fulfillmentPill(order.status);
  const stepIndex = FLOW.indexOf(order.status);
  const subtotal = order.items.reduce((s, i) => s + i.price * i.quantity, 0);
  const addr = order.shipping_address || {};
  const inputCls = "w-full bg-white border border-gray-300 rounded-lg text-sm px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E80071]/30 focus:border-[#E80071]";

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="max-w-[1000px] w-full" data-testid="order-detail">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={onBack} data-testid="order-back-btn" className="p-2 rounded-lg hover:bg-gray-200/60 text-gray-600 transition-colors" aria-label="Back to orders">
            <ArrowLeft size={17} />
          </button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-[#111827]">Order #{order.id.slice(0, 8).toUpperCase()}</h1>
            <p className="text-xs text-gray-500 mt-0.5">{new Date(order.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
          </div>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${pay.cls}`}>{pay.label}</span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${ful.cls}`}>{ful.label}</span>
        </div>
        <div className="flex items-center gap-2">
          {order.status === "awaiting_confirmation" && order.payment_method === "cod" && waPhone && (
            <a
              href={`https://wa.me/${waPhone}?text=${waText}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="order-whatsapp-confirm-btn"
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
            >
              <MessageCircle size={14} /> WhatsApp Confirm
            </a>
          )}
          {NEXT[order.status] && (
            <button onClick={advance} disabled={busy} data-testid="order-advance-btn" className="flex items-center gap-2 bg-[#E80071] hover:bg-[#D00065] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
              <Check size={14} /> Mark as {FLOW_LABELS[NEXT[order.status]]}
            </button>
          )}
          {order.status === "delivered" && (
            <button onClick={() => setShowRefund(true)} disabled={busy} data-testid="order-return-btn" className="flex items-center gap-2 bg-white hover:bg-purple-50 text-purple-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
              <RotateCcw size={14} /> Mark Returned
            </button>
          )}
          {!["delivered", "cancelled", "returned"].includes(order.status) && (
            <button onClick={cancel} disabled={busy} data-testid="order-cancel-btn" className="flex items-center gap-2 bg-white hover:bg-red-50 text-red-600 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
              <Ban size={14} /> Cancel
            </button>
          )}
        </div>
      </div>

      {!["cancelled", "returned"].includes(order.status) && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6" data-testid="order-stepper">
          <div className="flex items-center">
            {FLOW.map((s, i) => {
              const done = stepIndex >= i;
              return (
                <div key={s} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center gap-1.5">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center border-2 text-[10px] font-semibold ${done ? "bg-[#E80071] border-[#E80071] text-white" : "bg-white border-gray-300 text-gray-400"}`}>
                      {done ? <Check size={13} /> : i + 1}
                    </span>
                    <span className={`text-[10px] font-medium whitespace-nowrap ${done ? "text-gray-900" : "text-gray-400"}`}>{FLOW_LABELS[s]}</span>
                  </div>
                  {i < FLOW.length - 1 && <div className={`flex-1 h-0.5 mx-2 -mt-4 ${stepIndex > i ? "bg-[#E80071]" : "bg-gray-200"}`} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showRefund && order.status === "delivered" && (
        <div className="bg-purple-50 rounded-xl border border-purple-200 shadow-sm p-5 mb-6" data-testid="return-refund-panel">
          <p className="text-sm font-medium text-[#111827] mb-1">Mark as returned</p>
          <p className="text-xs text-gray-500 mb-4">
            Stock is added back automatically.
            {order.payment_method === "wallet" && " The refund is credited to the customer's ViRa Wallet instantly."}
            {order.payment_method === "card" && " The amount is recorded here — process the actual refund in your Stripe dashboard."}
            {order.payment_method === "cod" && " COD was collected by the courier — record any refund you arrange with the customer."}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">₹</span>
              <input
                type="number"
                min="0"
                max={order.total}
                step="0.01"
                value={refundAmt}
                onChange={(e) => setRefundAmt(e.target.value)}
                data-testid="refund-amount-input"
                className="w-32 bg-white border border-gray-300 rounded-lg text-sm px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-300"
              />
              <span className="text-xs text-gray-400">of {inr(order.total)}</span>
            </div>
            <button onClick={markReturned} disabled={busy} data-testid="return-confirm-btn" className="bg-purple-700 hover:bg-purple-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
              Confirm Return
            </button>
            <button onClick={() => setShowRefund(false)} data-testid="return-cancel-btn" className="text-sm text-gray-400 hover:text-gray-600">
              Never mind
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col gap-6">
          <Card title="Items" icon={Package} testid="order-items-card">
            <div className="divide-y divide-gray-100">
              {order.items.map((i) => (
                <div key={`${i.product_id}-${i.variant || ""}`} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0" data-testid={`order-item-${i.product_id}`}>
                  <div className="w-12 h-14 bg-gray-50 border border-gray-100 rounded-lg flex items-center justify-center shrink-0">
                    <img src={i.image} alt="" className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 leading-snug">{i.name}{i.variant ? ` — ${i.variant}` : ""}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{inr(i.price)} × {i.quantity}</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">{inr(i.price * i.quantity)}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Payment summary" icon={CreditCard} testid="order-payment-card">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
              {order.discount > 0 && (
                <div className="flex justify-between text-green-700"><span>Coupon {order.coupon_code}</span><span>−{inr(order.discount)}</span></div>
              )}
              <div className="flex justify-between text-gray-600"><span>Shipping</span><span>{order.shipping_charge > 0 ? inr(order.shipping_charge) : "Free"}</span></div>
              {order.cod_fee > 0 && (
                <div className="flex justify-between text-gray-600" data-testid="order-cod-fee-row"><span>COD fee</span><span>{inr(order.cod_fee)}</span></div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-100 font-semibold text-gray-900" data-testid="order-detail-total">
                <span>Total</span><span>{inr(order.total)}</span>
              </div>
              {order.refund_amount > 0 && (
                <div className="flex justify-between text-purple-700" data-testid="order-refund-row">
                  <span>Refund</span>
                  <span>
                    −{inr(order.refund_amount)} ·{" "}
                    {{ refunded_wallet: "wallet credited", pending_stripe: "process in Stripe", pending_manual: "manual" }[order.refund_status] || "recorded"}
                  </span>
                </div>
              )}
              <p className="text-xs text-gray-400 pt-1" data-testid="order-payment-method">
                {order.payment_method === "cod"
                  ? `Cash on Delivery — collect ${inr(order.total)} on delivery`
                  : order.payment_method === "wallet"
                    ? "Paid with ViRa Wallet"
                    : "Paid by card via Stripe"}
                {order.cashback ? ` · ${inr(order.cashback)} cashback earned` : ""}
              </p>
            </div>
          </Card>

          <Card title="Courier & tracking" icon={Truck} testid="order-tracking-card">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="Courier partner" data-testid="order-courier-input" className={inputCls} />
              <input value={trackingId} onChange={(e) => setTrackingId(e.target.value)} placeholder="Tracking ID / AWB" data-testid="order-tracking-input" className={inputCls} />
              <input value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} placeholder="Tracking URL" data-testid="order-tracking-url-input" className={inputCls} />
            </div>
            <button onClick={saveShipping} disabled={busy} data-testid="order-save-shipping-btn" className="mt-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
              Save shipping details
            </button>
          </Card>
        </div>

        <div className="lg:col-span-1 flex flex-col gap-6">
          <Card title="Customer" icon={User} testid="order-customer-card">
            <p className="text-sm font-medium text-gray-900">{order.customer_name || "Guest"}</p>
            {order.customer_email && <p className="text-sm text-gray-500 mt-1">{order.customer_email}</p>}
            {order.customer_phone && <p className="text-sm text-gray-500 mt-0.5">{order.customer_phone}</p>}
          </Card>

          <Card title="Shipping address" icon={MapPin} testid="order-address-card">
            {addr.line1 ? (
              <p className="text-sm text-gray-600 leading-relaxed">
                {addr.name && <span className="font-medium text-gray-900">{addr.name}<br /></span>}
                {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />
                {addr.city}, {addr.state} — {addr.pincode}
              </p>
            ) : (
              <p className="text-sm text-gray-400">No address on file.</p>
            )}
          </Card>

          <ShippingPanel order={order} onUpdated={onSaved} />
        </div>
      </div>
    </motion.div>
  );
};
